import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { 
  User, Mail, Shield, Camera, Lock, CheckCircle, 
  Loader2, Save, X 
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ProfileModal({ onClose }) {
  const { user, updateUser } = useAuth();
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Profile Info State
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    username: user?.username || ''
  });

  // Password State
  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Click away logic
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.put(`${API_URL}/api/auth/profile`, {
        full_name: formData.full_name,
        email: formData.email
      });
      updateUser(response.data.user);
      toast.success('Profile updated successfully!');
      setIsEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (passwords.new_password !== passwords.confirm_password) {
      return toast.error('Passwords do not match');
    }
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/auth/password`, {
        current_password: passwords.current_password,
        new_password: passwords.new_password
      });
      toast.success('Password updated successfully!');
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const uploadFormData = new FormData();
    uploadFormData.append('photo', file);

    setLoading(true);
    try {
      const response = await axios.put(`${API_URL}/api/auth/photo`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      updateUser({ profile_picture: response.data.profile_picture });
      toast.success('Profile picture updated!');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to upload photo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 md:p-6 bg-black/60 backdrop-blur-xl overflow-y-auto">
      <motion.div 
        ref={modalRef}
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="w-full max-w-4xl bg-[#F5F7F9] dark:bg-[#0F172A] rounded-[2.5rem] shadow-2xl overflow-hidden relative border border-white/20 dark:border-white/10"
      >
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-6 right-6 p-2 bg-white/50 dark:bg-black/20 hover:bg-white dark:hover:bg-black/40 rounded-full transition-all z-10 text-gray-800 dark:text-white"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="max-h-[90vh] overflow-y-auto custom-scrollbar p-8 pt-12">
          <div className="space-y-8">
            
            {/* Header Section */}
            <div className="flex flex-col md:flex-row items-center gap-8 bg-white dark:bg-[#1E293B] p-8 rounded-3xl shadow-sm border border-gray-100 dark:border-gray-800">
              <div className="relative group">
                <div className="w-32 h-32 rounded-3xl overflow-hidden ring-4 ring-[#D0021B]/10 dark:ring-[#D0021B]/20">
                  {user?.profile_picture ? (
                    <img 
                      src={`${API_URL}${user.profile_picture}`} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-tr from-[#D0021B] to-[#FF4D4D] flex items-center justify-center text-4xl font-bold text-white">
                      {user?.full_name?.charAt(0)}
                    </div>
                  )}
                </div>
                <button 
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute -bottom-2 -right-2 p-3 bg-white dark:bg-[#0F172A] shadow-xl rounded-2xl text-[#D0021B] hover:scale-110 transition-transform border border-gray-100 dark:border-gray-700"
                >
                  <Camera className="w-5 h-5" />
                </button>
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  onChange={handlePhotoUpload} 
                  className="hidden" 
                  accept="image/*"
                />
              </div>

              <div className="flex-1 text-center md:text-left">
                <h1 className="text-3xl font-serif font-bold text-[#1A1A1A] dark:text-white mb-2">{user?.full_name}</h1>
                <div className="flex flex-wrap justify-center md:justify-start gap-4 text-sm text-gray-500 dark:text-gray-400">
                  
                  <span className="flex items-center gap-1.5 px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
                    <User className="w-4 h-4" />
                    @{user?.username}
                  </span>
                </div>
              </div>

              {!isEditing && (
                <button 
                  onClick={() => setIsEditing(true)}
                  className="px-6 py-2.5 bg-[#D0021B] text-white rounded-xl shadow-lg shadow-[#D0021B]/20 hover:bg-[#A50015] transition-all flex items-center gap-2 font-medium"
                >
                  <User className="w-4 h-4" />
                  Edit Profile
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              
              {/* Profile Details Form */}
              <motion.div 
                layout
                className="bg-white dark:bg-[#1E293B] p-8 rounded-3xl shadow-sm border border-gray-100 dark:border-gray-800"
              >
                <div className="flex items-center gap-3 mb-8">
                  <div className="p-2.5 bg-red-50 dark:bg-red-900/20 rounded-xl text-[#D0021B]">
                    <User className="w-5 h-5" />
                  </div>
                  <h2 className="text-xl font-bold dark:text-white">Profile Information</h2>
                </div>

                <form onSubmit={handleProfileUpdate} className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Username</label>
                    <div className="relative">
                      <User className="absolute left-4 top-3.5 w-4 h-4 text-gray-400 dark:text-gray-500" />
                      <input 
                        type="text" 
                        value={formData.username} 
                        disabled 
                        className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 rounded-xl text-gray-500 dark:text-gray-400 cursor-not-allowed text-sm font-medium"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Full Name</label>
                    <div className="relative">
                      <User className="absolute left-4 top-3.5 w-4 h-4 text-gray-400 dark:text-gray-500" />
                      <input 
                        type="text" 
                        value={formData.full_name} 
                        onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                        disabled={!isEditing}
                        className="w-full pl-12 pr-4 py-3 bg-[#F8FAFC] dark:bg-gray-800 border border-transparent dark:border-gray-700 focus:border-[#D0021B] focus:ring-4 focus:ring-[#D0021B]/5 rounded-xl text-sm transition-all text-gray-900 dark:text-white font-medium disabled:opacity-70"
                        placeholder="Enter full name"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Email Address</label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-3.5 w-4 h-4 text-gray-400 dark:text-gray-500" />
                      <input 
                        type="email" 
                        value={formData.email} 
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        disabled={!isEditing}
                        className="w-full pl-12 pr-4 py-3 bg-[#F8FAFC] dark:bg-gray-800 border border-transparent dark:border-gray-700 focus:border-[#D0021B] focus:ring-4 focus:ring-[#D0021B]/5 rounded-xl text-sm transition-all text-gray-900 dark:text-white font-medium disabled:opacity-70"
                        placeholder="admin@example.com"
                      />
                    </div>
                  </div>

                  {isEditing && (
                    <div className="flex gap-3 pt-4">
                      <button 
                        type="button"
                        onClick={() => {
                          setIsEditing(false);
                          setFormData({
                            full_name: user?.full_name || '',
                            email: user?.email || '',
                            username: user?.username || ''
                          });
                        }}
                        className="flex-1 px-2 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl font-bold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <X className="w-4 h-4" />
                        Cancel
                      </button>
                      <button 
                        type="submit"
                        disabled={loading}
                        className="flex-1 px-2 py-2 bg-[#D0021B] text-white rounded-xl font-bold hover:bg-[#A50015] transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg shadow-[#D0021B]/10"
                      >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Save Changes
                      </button>
                    </div>
                  )}
                </form>
              </motion.div>

              {/* Security / Password Form */}
              <div className="bg-white dark:bg-[#1E293B] p-8 rounded-3xl shadow-sm border border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-3 mb-8">
                  <div className="p-2.5 bg-amber-50 dark:bg-amber-900/20 rounded-xl text-amber-600">
                    <Lock className="w-5 h-5" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Security Settings</h2>
                </div>

                <form onSubmit={handlePasswordUpdate} className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Current Password</label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-3.5 w-4 h-4 text-gray-400 dark:text-gray-500" />
                      <input 
                        type="password" 
                        value={passwords.current_password}
                        onChange={(e) => setPasswords({...passwords, current_password: e.target.value})}
                        className="w-full pl-12 pr-4 py-3 bg-[#F8FAFC] dark:bg-gray-800 border border-transparent dark:border-gray-700 focus:border-amber-500 focus:ring-4 focus:ring-amber-500/5 rounded-xl text-sm transition-all text-gray-900 dark:text-white font-medium"
                        placeholder="••••••••"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">New Password</label>
                    <div className="relative">
                      <Shield className="absolute left-4 top-3.5 w-4 h-4 text-gray-400 dark:text-gray-500" />
                      <input 
                        type="password" 
                        value={passwords.new_password}
                        onChange={(e) => setPasswords({...passwords, new_password: e.target.value})}
                        className="w-full pl-12 pr-4 py-3 bg-[#F8FAFC] dark:bg-gray-800 border border-transparent dark:border-gray-700 focus:border-amber-500 focus:ring-4 focus:ring-amber-500/5 rounded-xl text-sm transition-all text-gray-900 dark:text-white font-medium"
                        placeholder="••••••••"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Confirm New Password</label>
                    <div className="relative">
                      <CheckCircle className="absolute left-4 top-3.5 w-4 h-4 text-gray-400 dark:text-gray-500" />
                      <input 
                        type="password" 
                        value={passwords.confirm_password}
                        onChange={(e) => setPasswords({...passwords, confirm_password: e.target.value})}
                        className="w-full pl-12 pr-4 py-3 bg-[#F8FAFC] dark:bg-gray-800 border border-transparent dark:border-gray-700 focus:border-amber-500 focus:ring-4 focus:ring-amber-500/5 rounded-xl text-sm transition-all text-gray-900 dark:text-white font-medium"
                        placeholder="••••••••"
                        required
                      />
                    </div>
                  </div>

                  <div className="pt-4">
                    <button 
                      type="submit"
                      disabled={loading}
                      className="w-full px-6 py-3 bg-gray-900 dark:bg-white dark:text-gray-900 text-white rounded-xl font-bold hover:bg-black dark:hover:bg-gray-200 transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg shadow-black/5 dark:shadow-white/5"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                      Update Password
                    </button>
                  </div>
                </form>
              </div>

            </div>

            
          </div>
        </div>
      </motion.div>
    </div>
  );
}
